import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Book, Users, Home, Headphones, Music2, Menu, X, Bookmark, Video, GitBranch } from 'lucide-react';
import { useAudioVoice } from '../context/AudioVoiceContext';
import GitaLogo from './GitaLogo';

const navLinks = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/chapters', label: 'Chapters', icon: Book },
  { to: '/bookmarks', label: 'Bookmarks', icon: Bookmark },
  { to: '/speakers', label: 'Speakers', icon: Users },
  { to: '/listen', label: 'Listen', icon: Headphones, match: (path) => path === '/listen' },
  { to: '/listen/stream', label: 'Stream', icon: Music2, match: (path) => path === '/listen/stream' },
  { to: '/watch', label: 'Watch', icon: Video, match: (path) => path === '/watch' },
  { to: '/family', label: 'Family', icon: GitBranch }
];

const Navbar = () => {
  const location = useLocation();
  const { voice, setVoice } = useAudioVoice();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <nav className="app-navbar">
      <div className="app-navbar-inner">
        <Link to="/" className="app-navbar-brand" onClick={() => setMenuOpen(false)}>
          <GitaLogo size={36} className="app-navbar-logo-mark shrink-0" />
          <span className="app-navbar-title">Bhagavad Gita</span>
        </Link>

        <div className="app-navbar-desktop">
          <div className="app-navbar-voice">
            <label htmlFor="audio-voice-select" className="sr-only">
              Recitation voice
            </label>
            <select
              id="audio-voice-select"
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              aria-label="Narration voice"
              className="app-navbar-select"
            >
              <option value="male">Male voice</option>
              <option value="female">Female voice</option>
            </select>
          </div>

          <div className="app-navbar-links">
            {navLinks.map(({ to, label, icon: Icon, match }) => {
              const active = match ? match(location.pathname) : isActive(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`app-navbar-link ${active ? 'active' : ''}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="app-navbar-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <button
          type="button"
          className="app-navbar-scrim"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div id="mobile-nav-menu" className={`app-navbar-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="app-navbar-drawer-voice">
          <label htmlFor="audio-voice-select-mobile" className="text-sm text-gray-600">
            Recitation voice
          </label>
          <select
            id="audio-voice-select-mobile"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            className="app-navbar-select w-full mt-1"
          >
            <option value="male">Male voice</option>
            <option value="female">Female voice</option>
          </select>
        </div>

        <div className="app-navbar-drawer-links">
          {navLinks.map(({ to, label, icon: Icon, match }) => {
            const active = match ? match(location.pathname) : isActive(to);
            return (
              <Link
                key={to}
                to={to}
                className={`app-navbar-drawer-link ${active ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span>{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
